import Phaser from "phaser";

import { PREMIUM_SHOP_ART } from "../config/artAssets";
import type { DiamondSkinId, PremiumOfferKind } from "../domain/types";

export type PremiumShopRelic = "reserve" | "booster" | "skin" | "signet";

export type PremiumShopVisualState = {
  selectedRelic: PremiumShopRelic;
  offlineReserveSeconds: number;
  queuedBoosts: number;
  selectedSkinId: DiamondSkinId | null;
  previewSkinId: DiamondSkinId | null;
  startWindowPhase: "upcoming" | "active" | "expired";
  startWindowRemainingBasisPoints: number;
  reducedMotion: boolean;
};

export type PremiumShopSceneCallbacks = {
  onReady?(): void;
  onRelicSelected?(relic: PremiumShopRelic): void;
};

type RelicObject = {
  kind: PremiumShopRelic;
  image: Phaser.GameObjects.Image;
  baseScale: number;
  baseY: number;
};

const RELIC_ASSET = {
  reserve: PREMIUM_SHOP_ART.offlineReserve,
  booster: PREMIUM_SHOP_ART.passiveBooster,
  skin: PREMIUM_SHOP_ART.auroraSkin,
  signet: PREMIUM_SHOP_ART.seasonSignet,
} as const;

export function premiumRelicForOfferKind(kind: PremiumOfferKind): PremiumShopRelic {
  if (kind === "offline-reserve") return "reserve";
  if (kind === "passive-boost" || kind === "tap-boost" || kind === "energy-recovery-boost") return "booster";
  if (kind === "cosmetic") return "skin";
  return "signet";
}

export class PremiumShopScene extends Phaser.Scene {
  readonly #callbacks: PremiumShopSceneCallbacks;
  readonly #renderScale: number;
  #visualState: PremiumShopVisualState;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #relics: RelicObject[] = [];
  #sandStream!: Phaser.GameObjects.Rectangle;
  #sandGrains: Phaser.GameObjects.Arc[] = [];
  #sparks: Phaser.GameObjects.Arc[] = [];
  #startRing!: Phaser.GameObjects.Graphics;
  #startCharges: Phaser.GameObjects.Arc[] = [];
  #grantFlight: Phaser.GameObjects.Arc[] = [];
  #loadingSkinKey: string | null = null;

  constructor(
    callbacks: PremiumShopSceneCallbacks,
    visualState: PremiumShopVisualState,
    renderScale: number,
  ) {
    super({ key: "PremiumShop" });
    this.#callbacks = callbacks;
    this.#visualState = visualState;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(PREMIUM_SHOP_ART.background.key, PREMIUM_SHOP_ART.background.url);
    this.load.image(PREMIUM_SHOP_ART.offlineReserve.key, PREMIUM_SHOP_ART.offlineReserve.url);
    this.load.image(PREMIUM_SHOP_ART.passiveBooster.key, PREMIUM_SHOP_ART.passiveBooster.url);
    this.load.image(PREMIUM_SHOP_ART.seasonSignet.key, PREMIUM_SHOP_ART.seasonSignet.url);
    this.load.image(PREMIUM_SHOP_ART.auroraSkin.key, PREMIUM_SHOP_ART.auroraSkin.url);
    const skinIds = [this.#visualState.selectedSkinId, this.#visualState.previewSkinId]
      .filter((skinId): skinId is DiamondSkinId => skinId !== null);
    for (const skinId of new Set(skinIds)) {
      const asset = PREMIUM_SHOP_ART.skins[skinId];
      if (asset.key !== PREMIUM_SHOP_ART.auroraSkin.key) this.load.image(asset.key, asset.url);
    }
  }

  create(): void {
    this.#background = this.add.image(0, 0, PREMIUM_SHOP_ART.background.key).setDepth(0);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x062d36, 0.18).setOrigin(0).setDepth(1);
    this.#relics = (Object.keys(RELIC_ASSET) as PremiumShopRelic[]).map((kind) => {
      const asset = RELIC_ASSET[kind];
      // The scene spans the whole sheet underneath the accessible HTML controls.
      // Relics are deliberately visual-only: enabling Phaser input here lets the
      // same pointer select a hidden relic after a category button handles it.
      const image = this.add.image(0, 0, asset.key).setDepth(4);
      return { kind, image, baseScale: 1, baseY: 0 };
    });
    this.#sandStream = this.add
      .rectangle(0, 0, 1.3, 34, 0xc9fff5, 0.42)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(6)
      .setVisible(false);
    this.#sandGrains = Array.from({ length: 16 }, (_, index) =>
      this.add
        .circle(0, 0, index % 4 === 0 ? 1.2 : 0.85, index % 3 === 0 ? 0xffe6a1 : 0xb9fff6, 0.86)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(6)
        .setVisible(false),
    );
    this.#sparks = Array.from({ length: 22 }, (_, index) =>
      this.add
        .circle(0, 0, index % 4 === 0 ? 3 : 2, index % 3 === 0 ? 0xffd985 : 0x91fff5, 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(7)
        .setVisible(false),
    );
    this.#startRing = this.add.graphics().setDepth(6).setVisible(false);
    this.#startCharges = Array.from({ length: 3 }, (_, index) =>
      this.add
        .circle(0, 0, 3.2 + index * 0.9, index === 2 ? 0xffd985 : 0x86f1df, 0.82)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(7)
        .setVisible(false),
    );
    this.#grantFlight = Array.from({ length: 18 }, (_, index) =>
      this.add
        .circle(0, 0, index % 4 === 0 ? 3.2 : 2.1, index % 3 === 0 ? 0xffdb8c : 0x8ff9e5, 0.95)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(8)
        .setVisible(false),
    );
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyState();
    this.#callbacks.onReady?.();
  }

  override update(time: number): void {
    this.#updateHourglassSand(time);
    this.#updateStartWindow(time);
  }

  setVisualState(visualState: PremiumShopVisualState): void {
    if (
      this.#visualState.selectedRelic === visualState.selectedRelic &&
      this.#visualState.offlineReserveSeconds === visualState.offlineReserveSeconds &&
      this.#visualState.queuedBoosts === visualState.queuedBoosts &&
      this.#visualState.selectedSkinId === visualState.selectedSkinId &&
      this.#visualState.previewSkinId === visualState.previewSkinId &&
      this.#visualState.startWindowPhase === visualState.startWindowPhase &&
      this.#visualState.startWindowRemainingBasisPoints === visualState.startWindowRemainingBasisPoints &&
      this.#visualState.reducedMotion === visualState.reducedMotion
    ) return;
    this.#visualState = visualState;
    if (this.sys.isActive() && this.#ensureCurrentSkinTexture()) this.#applyState();
  }

  playPurchase(kind: PremiumShopRelic, grantBasisPoints = 0): void {
    if (!this.sys.isActive() || this.#visualState.reducedMotion) return;
    const relic = this.#relics.find((candidate) => candidate.kind === kind);
    if (relic === undefined) return;
    this.tweens.killTweensOf(relic.image);
    this.tweens.add({
      targets: relic.image,
      scaleX: relic.baseScale * 1.15,
      scaleY: relic.baseScale * 1.15,
      duration: 240,
      yoyo: true,
      ease: "Back.easeOut",
      onComplete: () => relic.image.setScale(relic.baseScale).setY(relic.baseY),
    });
    this.#emitBurst(relic.image.x, relic.image.y);
    if (kind === "signet" && grantBasisPoints > 0) {
      this.#emitGrantFlight(relic.image.x, relic.image.y, grantBasisPoints);
    }
  }

  debugSnapshot(): Record<string, unknown> {
    const selected = this.#relics.find((relic) => relic.kind === this.#visualState.selectedRelic);
    return {
      selectedRelic: this.#visualState.selectedRelic,
      selectedSkinId: this.#visualState.selectedSkinId,
      previewSkinId: this.#visualState.previewSkinId,
      selectedX: selected?.image.x ?? null,
      selectedY: selected?.image.y ?? null,
      selectedBaseY: selected?.baseY ?? null,
      selectedScale: selected?.image.scaleX ?? null,
      selectedBaseScale: selected?.baseScale ?? null,
      sandStreamVisible: this.#sandStream?.visible ?? false,
      visibleSandGrains: this.#sandGrains.filter((grain) => grain.visible).length,
      firstSandGrainY: this.#sandGrains[0]?.y ?? null,
      startWindowPhase: this.#visualState.startWindowPhase,
      startWindowRemainingBasisPoints: this.#visualState.startWindowRemainingBasisPoints,
      startRingVisible: this.#startRing?.visible ?? false,
      visibleStartCharges: this.#startCharges.filter((charge) => charge.visible).length,
      visibleGrantFlight: this.#grantFlight.filter((particle) => particle.visible).length,
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main.setViewport(0, 0, physicalWidth, physicalHeight).setZoom(this.#renderScale).centerOn(width / 2, height / 2);
    const scale = Math.max(width / PREMIUM_SHOP_ART.background.width, height / PREMIUM_SHOP_ART.background.height);
    this.#background.setPosition(width / 2, height / 2).setScale(scale);
    this.#shade.setSize(width, height);
    this.#applyState();
  }

  #applyState(): void {
    if (this.#layoutWidth === 0 || this.#layoutHeight === 0) return;
    this.#relics.forEach((relic) => this.tweens.killTweensOf(relic.image));
    const selectedIndex = this.#relics.findIndex((relic) => relic.kind === this.#visualState.selectedRelic);
    const selected = this.#relics[selectedIndex];
    if (selected === undefined) return;
    const sideRelics = this.#relics.filter((relic) => relic !== selected);
    const heroY = Math.min(314, this.#layoutHeight * 0.395);
    const sideHeroY = Math.min(292, this.#layoutHeight * 0.37);
    const selectedAsset = this.#assetForRelic(selected.kind);
    selected.image.setTexture(selectedAsset.key);
    selected.baseScale = Math.min(178 / selectedAsset.height, 188 / selectedAsset.width);
    selected.baseY = heroY;
    selected.image
      .setPosition(this.#layoutWidth / 2, heroY)
      .setAngle(0)
      .setAlpha(1)
      .setDepth(5)
      .setScale(selected.baseScale);
    const slots = [this.#layoutWidth * 0.18, this.#layoutWidth * 0.82, this.#layoutWidth * 0.5];
    sideRelics.forEach((relic, index) => {
      const asset = this.#assetForRelic(relic.kind);
      relic.image.setTexture(asset.key);
      relic.baseScale = Math.min(64 / asset.height, 66 / asset.width);
      relic.baseY = sideHeroY + (index === 2 ? 114 : 88);
      relic.image
        .setPosition(slots[index] ?? this.#layoutWidth / 2, relic.baseY)
        .setScale(relic.baseScale)
        .setAngle(0)
        .setAlpha(0.72)
        .setDepth(4);
    });
    this.#syncHourglassSand();
    this.#syncStartWindow();
  }

  #syncStartWindow(): void {
    const signet = this.#relics.find((relic) => relic.kind === "signet");
    const active = signet !== undefined && this.#visualState.selectedRelic === "signet";
    this.#startRing.setVisible(active);
    this.#startCharges.forEach((charge) => charge.setVisible(active));
    if (!active || signet === undefined) return;
    this.#startCharges.forEach((charge, index) => {
      charge.setPosition(
        signet.image.x + (index - 1) * 22,
        signet.baseY + 76,
      );
    });
    this.#drawStartRing(signet.image.x, signet.baseY);
  }

  #drawStartRing(x: number, y: number): void {
    const remaining = Phaser.Math.Clamp(this.#visualState.startWindowRemainingBasisPoints / 10_000, 0, 1);
    const radius = 83;
    this.#startRing.clear();
    this.#startRing.lineStyle(2, 0x3b827f, 0.42).strokeCircle(x, y, radius);
    if (remaining <= 0) return;
    const color = this.#visualState.startWindowPhase === "upcoming" ? 0x8db8b2 : 0xffd985;
    this.#startRing.lineStyle(4, color, 0.92);
    this.#startRing.beginPath();
    this.#startRing.arc(x, y, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * remaining, false);
    this.#startRing.strokePath();
  }

  #updateStartWindow(time: number): void {
    if (this.#visualState.selectedRelic !== "signet" || !this.#startRing.visible) return;
    const signet = this.#relics.find((relic) => relic.kind === "signet");
    if (signet === undefined) return;
    this.#drawStartRing(signet.image.x, signet.baseY);
    this.#startCharges.forEach((charge, index) => {
      const phase = time * 0.0022 + index * 0.82;
      const alpha = this.#visualState.reducedMotion ? 0.74 : 0.58 + Math.sin(phase) * 0.22;
      charge.setAlpha(this.#visualState.startWindowPhase === "expired" ? 0.25 : alpha);
      charge.setScale(this.#visualState.reducedMotion ? 1 : 0.92 + Math.sin(phase) * 0.08);
    });
  }

  #syncHourglassSand(): void {
    const reserve = this.#relics.find((relic) => relic.kind === "reserve");
    const active = reserve !== undefined && this.#visualState.selectedRelic === "reserve";
    this.#sandStream.setVisible(active);
    this.#sandGrains.forEach((grain) => grain.setVisible(active && !this.#visualState.reducedMotion));
    if (!active || reserve === undefined) return;
    this.#sandStream
      .setPosition(reserve.image.x, reserve.baseY + 17)
      .setSize(Math.max(1, reserve.baseScale * 1.65), reserve.baseScale * 47)
      .setAlpha(this.#visualState.reducedMotion ? 0.54 : 0.4);
  }

  #updateHourglassSand(time: number): void {
    const reserve = this.#relics.find((relic) => relic.kind === "reserve");
    if (reserve === undefined || this.#visualState.selectedRelic !== "reserve") return;
    if (this.#visualState.reducedMotion) return;
    const scale = reserve.image.scaleX;
    const top = reserve.image.y - 25 * scale;
    const neck = reserve.image.y + 1 * scale;
    const bottom = reserve.image.y + 48 * scale;
    this.#sandStream
      .setPosition(reserve.image.x, reserve.image.y + 17 * scale)
      .setSize(Math.max(1, scale * 1.65), scale * 47)
      .setAlpha(0.38 + Math.sin(time * 0.006) * 0.06);
    this.#sandGrains.forEach((grain, index) => {
      const phase = (time * 0.00034 + index / this.#sandGrains.length) % 1;
      const y = top + (bottom - top) * phase;
      const chamberWidth = y < neck
        ? ((neck - y) / Math.max(1, neck - top)) * 7
        : ((y - neck) / Math.max(1, bottom - neck)) * 10;
      const drift = Math.sin(index * 2.31 + phase * Math.PI * 5) * chamberWidth * scale;
      grain
        .setPosition(reserve.image.x + drift, y)
        .setAlpha(0.45 + Math.sin(index + phase * Math.PI) * 0.25)
        .setScale(0.72 + (index % 4) * 0.08);
    });
  }

  #assetForRelic(kind: PremiumShopRelic) {
    if (kind === "skin") {
      const skinId = this.#visualState.previewSkinId ?? this.#visualState.selectedSkinId;
      if (skinId !== null) return PREMIUM_SHOP_ART.skins[skinId];
    }
    return RELIC_ASSET[kind];
  }

  #ensureCurrentSkinTexture(): boolean {
    const skinId = this.#visualState.previewSkinId ?? this.#visualState.selectedSkinId;
    if (skinId === null) return true;
    const asset = PREMIUM_SHOP_ART.skins[skinId];
    if (this.textures.exists(asset.key)) return true;
    if (this.#loadingSkinKey !== null) return false;
    this.#loadingSkinKey = asset.key;
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.#loadingSkinKey = null;
      if (this.#ensureCurrentSkinTexture()) this.#applyState();
    });
    this.load.image(asset.key, asset.url);
    this.load.start();
    return false;
  }

  #emitBurst(x: number, y: number): void {
    this.#sparks.forEach((spark, index) => {
      const angle = (Math.PI * 2 * index) / this.#sparks.length;
      const distance = 54 + (index % 5) * 8;
      spark.setPosition(x, y).setVisible(true).setAlpha(1).setScale(1);
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance * 0.72,
        alpha: 0,
        scale: 0.3,
        duration: 560 + (index % 4) * 70,
        ease: "Cubic.easeOut",
        onComplete: () => spark.setVisible(false),
      });
    });
  }

  #emitGrantFlight(x: number, y: number, grantBasisPoints: number): void {
    const visibleCount = grantBasisPoints >= 5_000 ? 18 : grantBasisPoints >= 3_000 ? 13 : 8;
    this.#grantFlight.forEach((particle, index) => {
      this.tweens.killTweensOf(particle);
      if (index >= visibleCount) {
        particle.setVisible(false);
        return;
      }
      const lane = (index % 5) - 2;
      particle
        .setPosition(x + lane * 11, y + 26 + (index % 3) * 8)
        .setVisible(true)
        .setAlpha(0.95)
        .setScale(0.82 + (index % 4) * 0.08);
      this.tweens.add({
        targets: particle,
        x: this.#layoutWidth / 2 + lane * 4,
        y: 96 - (index % 4) * 5,
        alpha: 0,
        scale: 0.28,
        delay: (index % 6) * 34,
        duration: 620 + (index % 5) * 55,
        ease: "Sine.easeInOut",
        onComplete: () => particle.setVisible(false),
      });
    });
  }
}
